/** Primary label for a job — prefers job_name when set. */
export function formatJobName(job) {
  const name = (job?.job_name || '').trim();
  if (name) return name;
  const addr =
    [job?.delivery_address, job?.delivery_city, job?.delivery_state, job?.delivery_zip]
      .filter(Boolean)
      .join(', ') ||
    job?.job_location ||
    '';
  if (addr) {
    return addr.length > 55 ? `${addr.slice(0, 52)}…` : addr;
  }
  if (job?.id) return `Job ${String(job.id).substring(0, 8)}`;
  return 'Job';
}

/** Label for a previous job in a "copy from" dropdown. */
export function formatPreviousJobLabel(job) {
  if (!job) return '';
  const name = (job.job_name || '').trim();
  const parts = [];

  if (name) {
    parts.push(name);
  }

  const addr =
    [job.delivery_address, job.delivery_city, job.delivery_state, job.delivery_zip]
      .filter(Boolean)
      .join(', ') ||
    job.job_location ||
    '';
  if (addr) {
    const shortAddr = addr.length > 55 ? `${addr.slice(0, 52)}…` : addr;
    if (!name) {
      parts.push(shortAddr);
    } else if (shortAddr) {
      parts.push(shortAddr);
    }
  } else if (!name) {
    parts.push('Unknown location');
  }

  const fuel = [];
  if (Number(job.diesel_quantity) > 0) fuel.push(`${job.diesel_quantity} gal diesel`);
  if (Number(job.def_quantity) > 0) fuel.push(`${job.def_quantity} gal DEF`);
  if (Number(job.dyed_quantity) > 0) fuel.push(`${job.dyed_quantity} gal Dyed`);
  if (fuel.length) parts.push(fuel.join(', '));

  if (job.created_at) {
    parts.push(new Date(job.created_at).toLocaleDateString());
  }
  if (job.job_status) parts.push(job.job_status);

  return parts.join(' · ');
}

/** Map a job record to create-order form field values (does not include customer). */
export function jobToCreateFormFields(job) {
  if (!job) return null;

  let scheduledDate = '';
  if (job.scheduled_date) {
    try {
      scheduledDate = new Date(job.scheduled_date).toISOString().slice(0, 16);
    } catch {
      scheduledDate = '';
    }
  }

  return {
    job_name: job.job_name || '',
    delivery_address: job.delivery_address || '',
    delivery_city: job.delivery_city || '',
    delivery_state: job.delivery_state || 'WA',
    delivery_zip: job.delivery_zip || '',
    delivery_notes: job.delivery_notes || '',
    diesel_quantity:
      job.diesel_quantity != null && job.diesel_quantity !== '' ? String(job.diesel_quantity) : '',
    def_quantity: job.def_quantity != null && job.def_quantity !== '' ? String(job.def_quantity) : '',
    dyed_quantity: job.dyed_quantity != null && job.dyed_quantity !== '' ? String(job.dyed_quantity) : '',
    frequency: job.frequency || 'one-time',
    scheduled_date: scheduledDate,
  };
}

export function resolveJobCustomerId(job) {
  if (!job) return null;
  if (job.customer != null) {
    return typeof job.customer === 'string' ? job.customer : job.customer.id ?? job.customer;
  }
  return job.customer_id ?? null;
}

export function normalizeStreetAddressKey(job) {
  const street = String(job?.delivery_address || '').trim().toLowerCase();
  if (street) return street;
  const loc = String(job?.job_location || '').trim().toLowerCase();
  if (loc) return loc.split(',')[0].trim();
  return '';
}

function normalizeJobDedupeKey(job) {
  const name = String(job?.job_name || '').trim().toLowerCase();
  if (name) return `name:${name}`;
  const addr = normalizeStreetAddressKey(job);
  if (addr) return `addr:${addr}`;
  return `id:${job?.id || Math.random()}`;
}

/** Keep the most recent job per name or street address (jobs should be newest-first). */
export function dedupeJobsByStreetAddress(jobs) {
  if (!Array.isArray(jobs)) return [];
  const seen = new Set();
  const result = [];
  for (const job of jobs) {
    const key = normalizeJobDedupeKey(job);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(job);
  }
  return result;
}

export function filterJobsForCustomer(jobs, customerId) {
  if (!customerId || !Array.isArray(jobs)) return [];
  const target = String(customerId);
  const sorted = jobs
    .filter((job) => String(resolveJobCustomerId(job)) === target)
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  return dedupeJobsByStreetAddress(sorted);
}

export function getUniquePreviousJobs(jobs, customerId = null) {
  if (!Array.isArray(jobs)) return [];
  let filtered = jobs;
  if (customerId != null && customerId !== '') {
    const target = String(customerId);
    filtered = filtered.filter((job) => String(resolveJobCustomerId(job)) === target);
  }
  const sorted = [...filtered].sort(
    (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
  );
  return dedupeJobsByStreetAddress(sorted);
}
